import { OptionContract } from '../types';

export interface CachedOptionsData {
  symbol: string;
  currentPrice: number;
  expirations: Array<{
    date: string;
    daysToExpiration: number;
    contracts: OptionContract[];
  }>;
  timestamp: number;
  ttl: number;
}

class OptionsCache {
  private cache = new Map<string, CachedOptionsData>();
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes during market hours
  private readonly AFTER_HOURS_TTL = 30 * 60 * 1000; // 30 minutes after hours
  private readonly MAX_CACHE_SIZE = 50; // Maximum number of symbols to cache

  /**
   * Store options data in cache
   */
  set(symbol: string, data: Omit<CachedOptionsData, 'timestamp' | 'ttl'>): void {
    const now = Date.now();
    const ttl = this.isMarketHours() ? this.DEFAULT_TTL : this.AFTER_HOURS_TTL;

    const cachedData: CachedOptionsData = {
      ...data,
      timestamp: now,
      ttl: ttl
    };

    // Implement LRU eviction if cache is full
    if (this.cache.size >= this.MAX_CACHE_SIZE && !this.cache.has(symbol)) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(symbol, cachedData);
    this.cleanupExpired();

    console.log(`📦 Cached options for ${symbol}:`, {
      expirations: data.expirations.length,
      totalContracts: data.expirations.reduce((sum, exp) => sum + exp.contracts.length, 0),
      cacheSize: this.cache.size,
      ttl: ttl / 1000 + 's'
    });
  }

  /**
   * Get options data from cache
   */
  get(symbol: string): CachedOptionsData | null {
    const data = this.cache.get(symbol);

    if (!data) {
      return null;
    }

    // Check if data has expired
    const now = Date.now();
    if (now - data.timestamp > data.ttl) {
      this.cache.delete(symbol);
      console.log(`⏰ Cache expired for ${symbol}`);
      return null;
    }

    console.log(`✅ Cache hit for ${symbol}:`, {
      age: Math.round((now - data.timestamp) / 1000) + 's',
      expirations: data.expirations.length,
      contracts: data.expirations.reduce((sum, exp) => sum + exp.contracts.length, 0)
    });

    return data;
  }

  /**
   * Check if symbol is cached and not expired
   */
  has(symbol: string): boolean {
    return this.get(symbol) !== null;
  }

  /**
   * Filter cached options by expiration days range
   */
  getFilteredExpirations(
    symbol: string,
    minDays: number,
    maxDays: number
  ): Array<{ date: string; daysToExpiration: number; contracts: OptionContract[] }> {
    const data = this.get(symbol);
    if (!data) return [];

    return data.expirations.filter(exp =>
      exp.daysToExpiration > 1 && // Exclude expired/today options
      exp.daysToExpiration >= minDays &&
      exp.daysToExpiration <= maxDays
    );
  }

  /**
   * Filter cached options by delta range for a specific expiration
   */
  getFilteredContracts(
    symbol: string,
    expirationDate: string,
    minDelta?: number,
    maxDelta?: number
  ): OptionContract[] {
    const data = this.get(symbol);
    if (!data) return [];

    const expiration = data.expirations.find(exp => exp.date === expirationDate);
    if (!expiration) return [];

    let contracts = expiration.contracts;

    // Apply delta filtering if specified
    if (minDelta !== undefined && maxDelta !== undefined && minDelta > 0 && maxDelta > 0) {
      contracts = contracts.filter(contract => {
        if (contract.delta === undefined || contract.delta === null || contract.delta === 0) {
          return true; // Include contracts without delta data
        }
        const absDelta = Math.abs(contract.delta);
        return absDelta >= minDelta && absDelta <= maxDelta;
      });
    }

    return contracts;
  }

  /**
   * Get current price for a symbol
   */
  getCurrentPrice(symbol: string): number {
    const data = this.get(symbol);
    return data?.currentPrice || 0;
  }

  /**
   * Clear cache for a specific symbol
   */
  clear(symbol: string): void {
    this.cache.delete(symbol);
    console.log(`🗑️ Cleared cache for ${symbol}`);
  }

  /**
   * Clear all cached data
   */
  clearAll(): void {
    this.cache.clear();
    console.log('🗑️ Cleared all cache data');
  }

  /**
   * Get cache statistics
   */
  getStats(): {
    size: number;
    symbols: string[];
    memoryUsage: number;
    hitRate?: number;
  } {
    const symbols = Array.from(this.cache.keys());
    const memoryUsage = this.estimateMemoryUsage();

    return {
      size: this.cache.size,
      symbols,
      memoryUsage
    };
  }

  /**
   * Remove expired entries from cache
   */
  private cleanupExpired(): void {
    const now = Date.now();
    const expiredKeys: string[] = [];

    // Convert to array to avoid iteration issues
    Array.from(this.cache.entries()).forEach(([symbol, data]) => {
      if (now - data.timestamp > data.ttl) {
        expiredKeys.push(symbol);
      }
    });

    expiredKeys.forEach(key => {
      this.cache.delete(key);
      console.log(`🧹 Cleaned up expired cache for ${key}`);
    });
  }

  /**
   * Estimate memory usage of cache
   */
  private estimateMemoryUsage(): number {
    let totalSize = 0;

    Array.from(this.cache.values()).forEach(data => {
      // Rough estimation: each contract ~74 bytes + overhead
      const contractsSize = data.expirations.reduce(
        (sum: number, exp) => sum + exp.contracts.length * 74, 0
      );
      totalSize += contractsSize + 100; // Add overhead for metadata
    });

    return totalSize;
  }

  /**
   * Check if market is currently open (simplified)
   * TODO: Implement proper market hours checking
   */
  private isMarketHours(): boolean {
    const now = new Date();
    const day = now.getDay(); // 0 = Sunday, 6 = Saturday
    const hour = now.getHours();

    // Simple check: Monday-Friday, 9:30 AM - 4:00 PM ET
    // This is a simplified version - real implementation would need timezone handling
    return day >= 1 && day <= 5 && hour >= 9 && hour <= 16;
  }
}

// Export singleton instance
export const optionsCache = new OptionsCache();